import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { BoardWithCreator, BoardWithColumns, Column } from 'api/todo/types';
import { getBoard } from 'api/todo/todoApi';

interface ImportTaskDialogProps {
  open: boolean;
  boards: BoardWithCreator[];
  currentBoardId: number;
  onClose: () => void;
  onImport: (targetBoardId: number, targetColumnId: number) => Promise<void>;
}

export const ImportTaskDialog: React.FC<ImportTaskDialogProps> = ({
  open,
  boards,
  currentBoardId,
  onClose,
  onImport,
}) => {
  const [selectedBoardId, setSelectedBoardId] = useState<number>(0);
  const [selectedColumnId, setSelectedColumnId] = useState<number>(0);
  const [columns, setColumns] = useState<Column[]>([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Filter out the current board
  const availableBoards = boards.filter((b) => b.board_id !== currentBoardId);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedBoardId(0);
      setSelectedColumnId(0);
      setColumns([]);
      setError(null);
    }
  }, [open]);

  // Fetch columns when board is selected
  useEffect(() => {
    if (!selectedBoardId) {
      setColumns([]);
      setSelectedColumnId(0);
      return;
    }

    const fetchColumns = async () => {
      setLoadingColumns(true);
      setError(null);
      try {
        const board: BoardWithColumns = await getBoard(selectedBoardId);
        const sorted = [...board.columns].sort((a, b) => a.sort_order - b.sort_order);
        setColumns(sorted);
        if (sorted.length > 0) {
          setSelectedColumnId(sorted[0].column_id);
        }
      } catch (err) {
        setError('Failed to load board columns');
        setColumns([]);
      } finally {
        setLoadingColumns(false);
      }
    };

    fetchColumns();
  }, [selectedBoardId]);

  const handleImport = async () => {
    if (!selectedBoardId || !selectedColumnId) return;

    setImporting(true);
    setError(null);
    try {
      await onImport(selectedBoardId, selectedColumnId);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to import task');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          Import This Task Into Another Board
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            The task will appear on both boards and stay in sync. Any will automatically update everywhere.
          </Typography>
        </Box>
        {isMobile && (
          <IconButton onClick={onClose} edge="end" sx={{ mt: -0.5 }}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {availableBoards.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No other boards available to import to.
            </Typography>
          ) : (
            <>
              <FormControl fullWidth>
                <InputLabel>Target Board</InputLabel>
                <Select
                  value={selectedBoardId}
                  label="Target Board"
                  onChange={(e) => setSelectedBoardId(e.target.value as number)}
                >
                  <MenuItem value={0} disabled>Select a board</MenuItem>
                  {availableBoards.map((board) => (
                    <MenuItem key={board.board_id} value={board.board_id}>
                      {board.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {loadingColumns && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}

              {selectedBoardId > 0 && !loadingColumns && columns.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>Target Column</InputLabel>
                  <Select
                    value={selectedColumnId}
                    label="Target Column"
                    onChange={(e) => setSelectedColumnId(e.target.value as number)}
                  >
                    {columns.map((col) => (
                      <MenuItem key={col.column_id} value={col.column_id}>
                        {col.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}

          {error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ minHeight: { xs: 44, sm: 36 } }}>
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={!selectedBoardId || !selectedColumnId || importing || availableBoards.length === 0}
          sx={{ minHeight: { xs: 44, sm: 36 } }}
        >
          {importing ? <CircularProgress size={20} /> : 'Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
